import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../../assets/icons/clock-v2-icon.svg';
import { ReactComponent as CreditCardIcon } from '../../../assets/icons/credit-card.svg';
import { ReactComponent as GlobeIcon } from '../../../assets/icons/globe-icon.svg';
import { ReactComponent as ApplePayLogo } from '../../../assets/icons/payment-apple.svg';
import { ReactComponent as MastercardLogo } from '../../../assets/icons/payment-mastercard.svg';
import { ReactComponent as PayPalLogo } from '../../../assets/icons/payment-paypal.svg';
import { ReactComponent as StripeLogo } from '../../../assets/icons/payment-stripe.svg';
import { ReactComponent as VisaLogo } from '../../../assets/icons/payment-visa.svg';
import { ReactComponent as ReceiptIcon } from '../../../assets/icons/receipt-icon.svg';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { GatewayListApi } from '../../../interface/payment-transaction-interface';
import { useAppDispatch } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import MyButton from '../../../shared-components/button/Button';
import SidePaneModel from '../../../shared-components/table-side-panel/SidePaneModel';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import paymentGatewayApi from '../../../utils/api/v2/payment-gateway';

import AddGateway from './AddGateway';
import ViewGateway from './ViewGateway';

interface ModalInterface {
  title: string;
  content: JSX.Element;
  hideHeader: boolean;
}

/**
 * Country Name
 * @param {any} countryCode - The countryCode
 */
const getCountryName = (countryCode: string) => {
  const countries: Record<string, string> = {
    IN: 'India',
    SA: 'Saudi Arabia',
    US: 'United States',
    AE: 'UAE',
  };

  return countries[countryCode] || countryCode;
};

/**
 * Mada logo component
 * @returns {any} React node
 */
function MadaLogoComponent() {
  return (
    <div className="mada-logo-inline method-icon" title="Mada">
      mada
    </div>
  );
}

/**
 * Payment Gateway List Component
 * @returns {any} React node
 */
function PaymentGatewayList() {
  const { activePortal } = usePortal();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [gatewayList, setGatewayList] = useState<GatewayListApi[]>([]);

  const [sidePanelStatus, setSidePanelStatus] = useState<'open' | 'close'>('close');

  const [sidePanelView, setSidePanelView] = useState<ModalInterface>({
    title: '',
    content: <div />,
    hideHeader: false,
  });

  /**
   * Fetch Gateway List API
   */
  const getGatewayList = async () => {
    try {
      const response = await paymentGatewayApi.fetchPaymentGatewayConfigList();

      console.log(response);

      setGatewayList(response.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Open add gateway side panel
   */
  const handleAddNew = () => {
    setSidePanelStatus('open');

    setSidePanelView({
      title: 'Add New Gateway',
      hideHeader: false,
      content: (
        <AddGateway
          onCancel={() => setSidePanelStatus('close')}
          onSuccess={() => {
            setSidePanelStatus('close');
            getGatewayList();
          }}
        />
      ),
    });
  };

  /**
   * Open view gateway details side panel
   * @param {any} gateway The gateway details
   */
  const handleViewGateway = (gateway: GatewayListApi) => {
    setSidePanelStatus('open');

    /**
     * Edit gateway d
     */
    const handleEditFlow = () => {
      setSidePanelView((previous) => ({
        ...previous,
        title: 'Edit Gateway',
        hideHeader: false,
        content: (
          <AddGateway
            editData={{
              key: gateway.gatewayKey,
              businessType:
                gateway.useCases?.includes('b2c') && gateway.useCases?.includes('b2b')
                  ? 'both'
                  : gateway.useCases?.[0] || 'B2B',
              country: gateway.country,
              paymentMethods: gateway.paymentMethods || [],
              credentials: gateway.credentials,
            }}
            onCancel={() => setSidePanelStatus('close')}
            onSuccess={() => {
              setSidePanelStatus('close');
              getGatewayList();
            }}
          />
        ),
      }));
    };

    setSidePanelView({
      title: '',
      hideHeader: true,
      content: (
        <ViewGateway
          gateway={gateway}
          onEdit={handleEditFlow}
          onClose={() => setSidePanelStatus('close')}
          onStatusChange={getGatewayList}
        />
      ),
    });
  };

  /**
   * Render gateway logo
   * @param {any} name - The name
   */
  const renderGatewayLogo = (name: string) => {
    if (name.toLowerCase() === 'stripe') {
      return <StripeLogo className="gateway-logo-svg" />;
    }

    return <div className="gateway-name">{name}</div>;
  };

  /**
   * Render payment methods
   * @param {any} methods - The methods
   */
  const renderPaymentMethods = (methods: string[]) => {
    const formattedMethods = methods.map((m) => m.toLowerCase());

    const icons: React.ReactNode[] = [];

    // 1. Mada
    if (formattedMethods.includes('mada')) {
      icons.push(<MadaLogoComponent key="mada" />);
    }

    // 2. Debit card / Visa / Mastercard group
    if (
      formattedMethods.includes('debit_card') ||
      formattedMethods.includes('debit card') ||
      formattedMethods.includes('visa')
    ) {
      icons.push(
        <span key="visa" title="Visa" className="method-icon-tooltip">
          <VisaLogo className="method-icon" />
        </span>,
      );
      icons.push(
        <span key="mastercard" title="Mastercard" className="method-icon-tooltip">
          <MastercardLogo className="method-icon" />
        </span>,
      );
    }

    // 3. PayPal
    if (formattedMethods.includes('paypal')) {
      icons.push(
        <span key="paypal" title="PayPal" className="method-icon-tooltip">
          <PayPalLogo className="method-icon" />
        </span>,
      );
    }

    // 4. Apple Pay
    if (formattedMethods.includes('apple_pay') || formattedMethods.includes('apple pay')) {
      icons.push(
        <span key="apple" title="Apple Pay" className="method-icon-tooltip">
          <ApplePayLogo className="method-icon" />
        </span>,
      );
    }

    // 5. Credit Card (outline logo)
    if (formattedMethods.includes('credit_card') || formattedMethods.includes('credit card')) {
      icons.push(
        <span key="credit_card" title="Credit Card" className="method-icon-tooltip">
          <CreditCardIcon className="method-icon card-outline-icon" />
        </span>,
      );
    }

    // 6. Dynamic fallback for other payment methods without dedicated SVG icons (e.g. google_pay, stc_pay)
    const standardLogoMethods = [
      'credit_card',
      'credit card',
      'debit_card',
      'debit card',
      'mada',
      'visa',
      'paypal',
      'apple_pay',
      'apple pay',
    ];

    methods.forEach((method) => {
      const lower = method.toLowerCase();
      if (!standardLogoMethods.includes(lower)) {
        const labelMap: Record<string, string> = {
          googlePay: 'Google Pay',
          stcPay: 'STC Pay',
        };
        const label =
          labelMap[lower] ||
          method
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        const firstLetter = label.charAt(0).toUpperCase();

        icons.push(
          <div key={method} className="method-icon round-letter-logo" title={label}>
            {firstLetter}
          </div>,
        );
      }
    });

    return <div className="payment-methods-icons">{icons}</div>;
  };

  useEffect(() => {
    getGatewayList();
  }, [activePortal]);

  const formattedCount = String(gatewayList?.length || 0).padStart(2, '0');

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: 'Gateway List',
        count: formattedCount,
      }),
    );
  }, [dispatch, formattedCount]);

  return (
    <div className="payment-gateway-list-container">
      <SidePaneModel
        open={sidePanelStatus === 'open'}
        title={sidePanelView.title}
        content={sidePanelView.content}
        closePanel={() => setSidePanelStatus('close')}
        hideHeader={sidePanelView.hideHeader}
      />

      <div className="header-section">
        <div className="header-content">
          <h2>Payment Gateway Configuration</h2>

          <p>Configure your payment gateway for B2B Payouts & B2C Payments</p>
        </div>

        <div className="header-actions">
          {permissionCheck('mobilityPaymentTransactionView') && (
              <MyButton
                label="Payment Transactions"
                buttonType="outline"
                onClickFunc={() => navigate('/ev-admin/configuration/PaymentGateway/transactions')}
                disableButton={
                  activePortal === PortalType.NETWORKS
                    ? !(
                      permissionCheck('cpoPaymentTransactionView')
                    )
                    : !permissionCheck('mobilityPaymentTransactionView')
                }
              />
            )}
          <MyButton
            label="+ Add Gateway"
            buttonType="submit"
            onClickFunc={handleAddNew}
            disableButton={
              activePortal === PortalType.NETWORKS
                ? !(
                  permissionCheck('cpoPaymentGatewayAdd') ||
                  permissionCheck('cpoPaymentGatewayUpdate')
                )
                : !permissionCheck('mobilityPaymentGatewayAdd')
            }
          />
        </div>
      </div>

      <div className="gateway-list-wrapper">
        <div className="gateway-cards">
          {gatewayList?.map((gateway, index) => (
            <div
              className="gateway-card"
              key={index}
              onClick={() => handleViewGateway(gateway)}
              role="presentation">
              <div className="gateway-card-header">
                <div className="gateway-logo-wrapper">{renderGatewayLogo(gateway.gatewayKey)}</div>

                <div className={`status-pill ${gateway.isActive ? 'active' : 'inactive'}`}>
                  <span className="status-pill-dot" />
                  {gateway.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>

              <div className="gateway-info-rows">
                {/* Payment Methods */}
                <div className="info-row">
                  <div className="info-label">
                    <CreditCardIcon className="info-label-icon" />
                    Payment Method
                  </div>

                  <div className="info-value">{renderPaymentMethods(gateway.paymentMethods)}</div>
                </div>

                {/* Country */}
                <div className="info-row">
                  <div className="info-label">
                    <GlobeIcon className="info-label-icon" />
                    Country
                  </div>

                  <div className="info-value">
                    <div className="country-tags">
                      <span>{getCountryName(gateway.country)}</span>
                    </div>
                  </div>
                </div>

                {/* Business Type */}
                <div className="info-row">
                  <div className="info-label">
                    <ReceiptIcon className="info-label-icon" />
                    Payment Flow
                  </div>

                  <div className="info-value">
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      {['b2c', 'b2b'].map((type) => {
                        const isSupported = gateway.useCases?.includes(type);
                        if (!isSupported) return null;

                        const isActive =
                          Boolean(gateway.isActive) &&
                          (gateway.activeUseCases ? gateway.activeUseCases.includes(type) : true);

                        return (
                          <div key={type} className="business-status-wrap">
                            <div className="business-badge-row">
                              <div
                                className={`business-badge-v2 ${type} ${isActive ? 'active' : 'inactive'}`}>
                                <span className="badge-icon-circle">{isActive ? '✓' : ''}</span>
                                <span className="badge-text">
                                  {type.toLowerCase() === 'b2c' ? 'B2C Payment' : 'B2B Payout'}
                                </span>
                              </div>
                              <span
                                className={`active-status-text ${isActive ? 'active' : 'inactive'} ${type}`}>
                                {isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <span className="business-desc-text">
                              {isActive ? 'Enabled for transactions' : 'Not configured'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="gateway-card-footer">
                <ClockIcon className="footer-clock-icon" />
                Last Updated: {new Date(gateway.updatedAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PaymentGatewayList;
